<template>
  <div class="wos-scaling">
    <div class="row scaling-boxes">
      <div class="empty-box">
        <div class="row weight-box-title">
          <span> {{ $t("scale.weighing_on_scale.empty_weight") }}</span>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-left">
            <label class="control-label">{{ $t("common.scale") }}</label>
            <DxSelectBox
              v-model="localData.empty_scale_uuid"
              :data-source="[{ name: '', uuid: null }, ...scaleOptions]"
              display-expr="name"
              value-expr="uuid"
              :on-value-changed="onEmptyScaleChanged"
              placeholder=""
            />
          </div>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-left">
            <label class="control-label">{{ $t("scale.weighing_on_scale.weight") }}</label>
            <div
              v-if="isEmptyWeightReadOnly"
              class="sm-select-input-dialog-btn"
              @click="openPasswordDialog('empty')"
            >
              <div class="sm-select-input-dialog-info">
                <div class="sm-select-input-dialog-value">{{ localData.empty_weight_kg != null ? localData.empty_weight_kg + ' Kg' : '-' }}</div>
              </div>
              <div class="sm-select-input-dialog-icon">
                <i class="fa-regular fa-edit"></i>
              </div>
            </div>
            <DxNumberBox
              v-else
              v-model="localData.empty_weight_kg"
              :min="0"
              :showSpinButtons="false"
              class="dx-form-control"
              :format="quantityFormatter"
              placeholder="0.00"
              @input="onEmptyWeightKeyUp"
            />
            <div class="weight-status-row">
              <span :class="getWeightStatusClass(emptyWeightLoadState)">
                {{ getWeightStatusMessage(emptyWeightLoadState) }}
              </span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-center">
            <button
              :class="['btn', 'btn-outline-primary', { 'btn-disbled': !canWeighEmpty }]"
              @click="onWeighEmpty"
              :disabled="!canWeighEmpty"
            >
              {{ $t("scale.weighing_on_scale.weighing_on_scale") }}
            </button>
          </div>
        </div>
        <div class="row measurement-rows">
          <div class="col-md-4">
            <div class="right-box-label-content">
              <span>{{ $t("scale.weighing_on_scale.date_time") }}</span>
            </div>
          </div>
          <div class="col-md-5">
            <div class="right-box-value-content">
              <span>
                {{ localData.empty_datetime ?? "-" }}
              </span>
            </div>
          </div>
        </div>
        <div class="row measurement-rows">
          <div class="col-md-4">
            <div class="right-box-label-content">
              <span>{{ $t("common.weighing_type") }}</span>
            </div>
          </div>
          <div class="col-md-4">
            <div class="right-box-value-content">
              <span>
                {{ empty_weighing_type }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="full-box">
        <div class="row weight-box-title">
          <span> {{ fullWeightBoxTitle }}</span>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-left">
            <label class="control-label">{{ $t("common.scale") }}</label>
            <DxSelectBox
              v-model="localData.full_scale_uuid"
              :data-source="[{ name: '', uuid: null }, ...scaleOptions]"
              display-expr="name"
              value-expr="uuid"
              :on-value-changed="onFullScaleChanged"
              placeholder=""
            />
          </div>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-left">
            <label class="control-label">{{ $t("scale.weighing_on_scale.weight") }}</label>
            <div
              v-if="isFullWeightReadOnly"
              class="sm-select-input-dialog-btn"
              @click="openPasswordDialog('full')"
            >
              <div class="sm-select-input-dialog-info">
                <div class="sm-select-input-dialog-value">{{ localData.full_weight_kg != null ? localData.full_weight_kg + ' Kg' : '-' }}</div>
              </div>
              <div class="sm-select-input-dialog-icon">
                <i class="fa-regular fa-edit"></i>
              </div>
            </div>
            <DxNumberBox
              v-else
              v-model="localData.full_weight_kg"
              :min="0"
              :showSpinButtons="false"
              class="dx-form-control"
              :format="quantityFormatter"
              placeholder="0.00"
              @input="onFullWeightKeyUp"
            />
            <div class="weight-status-row">
              <span :class="getWeightStatusClass(fullWeightLoadState)">
                {{ getWeightStatusMessage(fullWeightLoadState) }}
              </span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-center">
            <button
              :class="['btn', 'btn-outline-primary', { 'btn-disbled': !canWeighFull }]"
              @click="onWeighFull"
              :disabled="!canWeighFull"
            >
              {{ $t("scale.weighing_on_scale.weighing_on_scale") }}
            </button>
          </div>
        </div>
        <div class="row measurement-rows">
          <div class="col-md-4">
            <div class="right-box-label-content">
              <span>{{ $t("scale.weighing_on_scale.date_time") }}</span>
            </div>
          </div>
          <div class="col-md-5">
            <div class="right-box-value-content">
              <span>
                {{ localData.full_datetime ?? "-" }}
              </span>
            </div>
          </div>
        </div>
        <div class="row measurement-rows">
          <div class="col-md-4">
            <div class="right-box-label-content">
              <span>{{ $t("common.weighing_type") }}</span>
            </div>
          </div>
          <div class="col-md-4">
            <div class="right-box-value-content">
              <span>
                {{ full_weighing_type }}
              </span>
            </div>
          </div>
        </div>
        <div class="row different-quantity-row" v-if="showDeliveryFeature">
          <div class="form-group col-sm-12 col-lg-12 text-left">
            <DxCheckBox
              v-model="localData.different_quantity_for_delivery"
              :text="$t('scale.weighing_on_scale.different_quantity_for_delivery')"
              @value-changed="onDifferentQuantityForDeliveryChanged"
            />
          </div>
        </div>
      </div>
      <div class="delivery-box" v-if="showDeliveryFeature && localData.different_quantity_for_delivery">
        <div class="row weight-box-title">
          <span> {{ $t("scale.weighing_on_scale.full_weight_for_delivery") }}</span>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-left">
            <label class="control-label">{{ $t("common.scale") }}</label>
            <DxSelectBox
              v-model="localData.full_delivery_scale_uuid"
              :data-source="[{ name: '', uuid: null }, ...scaleOptions]"
              display-expr="name"
              value-expr="uuid"
              :on-value-changed="onDeliveryScaleChanged"
              placeholder=""
            />
          </div>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-left">
            <label class="control-label">{{ $t("scale.weighing_on_scale.weight") }}</label>
            <div
              v-if="isDeliveryWeightReadOnly"
              class="sm-select-input-dialog-btn"
              @click="openPasswordDialog('delivery')"
            >
              <div class="sm-select-input-dialog-info">
                <div class="sm-select-input-dialog-value">{{ localData.full_delivery_weight_kg != null ? localData.full_delivery_weight_kg + ' Kg' : '-' }}</div>
              </div>
              <div class="sm-select-input-dialog-icon">
                <i class="fa-regular fa-edit"></i>
              </div>
            </div>
            <DxNumberBox
              v-else
              v-model="localData.full_delivery_weight_kg"
              :min="0"
              :showSpinButtons="false"
              class="dx-form-control"
              :format="quantityFormatter"
              placeholder="0.00"
              @input="onDeliveryWeightKeyUp"
            />
            <div class="weight-status-row">
              <span :class="getWeightStatusClass(deliveryWeightLoadState)">
                {{ getWeightStatusMessage(deliveryWeightLoadState) }}
              </span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="form-group col-sm-12 col-lg-12 text-center">
            <button
              :class="['btn', 'btn-outline-primary', { 'btn-disbled': !canWeighDelivery }]"
              @click="onWeighDelivery"
              :disabled="!canWeighDelivery"
            >
              {{ $t("scale.weighing_on_scale.weighing_on_scale") }}
            </button>
          </div>
        </div>
        <div class="row measurement-rows">
          <div class="col-md-4">
            <div class="right-box-label-content">
              <span>{{ $t("scale.weighing_on_scale.date_time") }}</span>
            </div>
          </div>
          <div class="col-md-5">
            <div class="right-box-value-content">
              <span>
                {{ localData.full_delivery_datetime ?? "-" }}
              </span>
            </div>
          </div>
        </div>
        <div class="row measurement-rows">
          <div class="col-md-4">
            <div class="right-box-label-content">
              <span>{{ $t("common.weighing_type") }}</span>
            </div>
          </div>
          <div class="col-md-4">
            <div class="right-box-value-content">
              <span>
                {{ delivery_weighing_type }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row load-mass">
      <span> {{ $t("scale.weighing_on_scale.load_weight") }} {{ load_mass }} <strong>Kg</strong></span>
      <span v-if="showDeliveryFeature && localData.different_quantity_for_delivery" class="load-mass-delivery">
        {{ $t("scale.weighing_on_scale.load_weight_for_delivery") }} {{ delivery_load_mass }} <strong>Kg</strong>
      </span>
    </div>

    <PasswordDialog
      :visible="passwordDialog.visible"
      :expected-password="currentDialogPassword"
      @confirmed="onPasswordConfirmed"
      @update:visible="passwordDialog.visible = $event"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxNumberBox } from "devextreme-vue/number-box";
import { DxCheckBox } from "devextreme-vue/check-box";
import { getQuantityFormatter } from "~/helpers/stockHelper";
import PasswordDialog from "./PasswordDialog.vue";
//import { required, requiredIf } from "vuelidate/lib/validators";
import moment from "moment";
import { WEIGHING_TYPES, FIELD_REPORT_MODES } from "@/pages/scale/config/weighingTypes";

export default {
  name: "scaling",
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localData: { ...this.formData },
      emptyWeightLoadState: "",
      fullWeightLoadState: "",
      deliveryWeightLoadState: "",
      emptyWeightUnlocked: false,
      fullWeightUnlocked: false,
      deliveryWeightUnlocked: false,
      passwordDialog: {
        visible: false,
        which: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      scales: "scale/scales",
      scaleMeasurements: "scale/scaleMeasurements",
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    quantityFormatter() {
      return getQuantityFormatter("Kg");
    },
    scaleOptions() {
      return Array.isArray(this.scales) ? this.scales : [];
    },
    load_mass() {
      if ((this.localData.full_weight_kg ?? 0) > 0 && ((this.localData.empty_weight_kg ?? 0) >= 0 && this.localData.empty_datetime)) {
        return (this.localData.full_weight_kg ?? 0) - (this.localData.empty_weight_kg ?? 0);
      }
      return 0;
    },
    delivery_load_mass() {
      if ((this.localData.full_delivery_weight_kg ?? 0) > 0 && ((this.localData.empty_weight_kg ?? 0) >= 0 && this.localData.empty_datetime)) {
        return (this.localData.full_delivery_weight_kg ?? 0) - (this.localData.empty_weight_kg ?? 0);
      }
      return 0;
    },
    full_weighing_type() {
      const scale = this.scaleOptions.find((scale) => scale.uuid === this.localData.full_scale_uuid);
      return scale ? scale.weighing_type : "-";
    },
    empty_weighing_type() {
      const scale = this.scaleOptions.find((scale) => scale.uuid === this.localData.empty_scale_uuid);
      return scale ? scale.weighing_type : "-";
    },
    delivery_weighing_type() {
      const scale = this.scaleOptions.find((scale) => scale.uuid === this.localData.full_delivery_scale_uuid);
      return scale ? scale.weighing_type : "-";
    },
    showDeliveryFeature() {
      return (
        Number(this.localData.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT &&
        Number(this.localData.field_report_type) === FIELD_REPORT_MODES.WITH_SALE
      );
    },
    fullWeightBoxTitle() {
      return this.showDeliveryFeature && this.localData.different_quantity_for_delivery
        ? this.$t("scale.weighing_on_scale.full_weight_for_reception")
        : this.$t("scale.weighing_on_scale.full_weight");
    },
    canWeighEmpty() {
      return !!this.localData.empty_scale_uuid && this.emptyWeightLoadState !== "loading";
    },
    canWeighFull() {
      return !!this.localData.full_scale_uuid && this.fullWeightLoadState !== "loading";
    },
    canWeighDelivery() {
      return !!this.localData.full_delivery_scale_uuid && this.deliveryWeightLoadState !== "loading";
    },
    currentDialogPassword() {
      if (!this.passwordDialog.which) return null;
      const scaleUuid =
        this.passwordDialog.which === "empty"
          ? this.localData.empty_scale_uuid
          : this.passwordDialog.which === "full"
          ? this.localData.full_scale_uuid
          : this.localData.full_delivery_scale_uuid;
      const scale = this.scaleOptions.find((s) => s.uuid === scaleUuid);
      return scale?.password || null;
    },
    isEmptyWeightReadOnly() {
      if (this.emptyWeightUnlocked) return false;
      const scale = this.scaleOptions.find((s) => s.uuid === this.localData.empty_scale_uuid);
      return !!(scale?.password);
    },
    isFullWeightReadOnly() {
      if (this.fullWeightUnlocked) return false;
      const scale = this.scaleOptions.find((s) => s.uuid === this.localData.full_scale_uuid);
      return !!(scale?.password);
    },
    isDeliveryWeightReadOnly() {
      if (this.deliveryWeightUnlocked) return false;
      const scale = this.scaleOptions.find((s) => s.uuid === this.localData.full_delivery_scale_uuid);
      return !!(scale?.password);
    },
  },
  components: {
    DxSelectBox,
    DxNumberBox,
    DxCheckBox,
    PasswordDialog,
  },
  methods: {
    ...mapActions({
      getScaleMeasurement: "scale/getScaleMeasurement",
      getScales: "scale/getScales",
    }),
    normalizeIsDefault(value) {
      return value === true || value === 1 || value === "1" || value === "true" || value === "TRUE";
    },
    isScaleInCurrentHouse(scaleUuid) {
      if (!scaleUuid) {
        return false;
      }

      return this.scaleOptions.some((scale) => scale.uuid === scaleUuid);
    },
    getPreferredScaleUuid() {
      if (!Array.isArray(this.scaleOptions) || this.scaleOptions.length === 0) {
        return null;
      }

      const defaultScale = this.scaleOptions.find((scale) => this.normalizeIsDefault(scale?.is_default));
      if (defaultScale?.uuid) {
        return defaultScale.uuid;
      }

      return this.scaleOptions.length === 1 ? this.scaleOptions[0]?.uuid || null : null;
    },
    applyDefaultScaleSelection(forceSelection = false) {
      if (!this.localData?.scale_house_uuid || !Array.isArray(this.scaleOptions) || this.scaleOptions.length === 0) {
        return;
      }

      const preferredScaleUuid = this.getPreferredScaleUuid();
      if (!preferredScaleUuid) {
        return;
      }

      const hasValidEmpty = this.isScaleInCurrentHouse(this.localData.empty_scale_uuid);
      const hasValidFull = this.isScaleInCurrentHouse(this.localData.full_scale_uuid);

      if (forceSelection || !hasValidEmpty) {
        this.localData.empty_scale_uuid = preferredScaleUuid;
      }

      if (forceSelection || !hasValidFull) {
        this.localData.full_scale_uuid = preferredScaleUuid;
      }

    },
    openPasswordDialog(which) {
      this.passwordDialog = { visible: true, which };
    },
    onPasswordConfirmed() {
      if (this.passwordDialog.which === "empty") {
        this.emptyWeightUnlocked = true;
      } else if (this.passwordDialog.which === "full") {
        this.fullWeightUnlocked = true;
      } else {
        this.deliveryWeightUnlocked = true;
      }
    },
    onEmptyScaleChanged(e) {
      this.emptyWeightLoadState = "";
      this.emptyWeightUnlocked = false;
      this.localData.empty_weight_kg = null;
    },
    async onWeighEmpty() {
      if (!this.canWeighEmpty || this.emptyWeightLoadState === "loading") {
        return;
      }

      this.emptyWeightLoadState = "loading";

      const hasMeasurement = await this.fetchScaleMeasurementAndApply(this.localData.empty_scale_uuid, "empty_weight_kg", "empty_datetime");

      this.emptyWeightLoadState = hasMeasurement ? "success" : "error";
    },
    onEmptyWeightKeyUp(e) {
      this.emptyWeightLoadState = "";
      this.localData.empty_datetime = moment().format(this.getDefaultDateFormat + " HH:mm:ss");
    },
    onFullScaleChanged(e) {
      this.fullWeightLoadState = "";
      this.fullWeightUnlocked = false;
      this.localData.full_weight_kg = null;
    },
    async onWeighFull() {
      if (!this.canWeighFull || this.fullWeightLoadState === "loading") {
        return;
      }

      this.fullWeightLoadState = "loading";

      const hasMeasurement = await this.fetchScaleMeasurementAndApply(this.localData.full_scale_uuid, "full_weight_kg", "full_datetime");

      this.fullWeightLoadState = hasMeasurement ? "success" : "error";
    },
    onFullWeightKeyUp(e) {
      this.fullWeightLoadState = "";
      this.localData.full_datetime = moment().format(this.getDefaultDateFormat + " HH:mm:ss");
    },
    onDifferentQuantityForDeliveryChanged(e) {
      if (!e.value) {
        this.deliveryWeightLoadState = "";
        this.deliveryWeightUnlocked = false;
        this.localData.full_delivery_scale_uuid = null;
        this.localData.full_delivery_weight_kg = null;
        this.localData.full_delivery_datetime = null;
      }
    },
    onDeliveryScaleChanged(e) {
      this.deliveryWeightLoadState = "";
      this.deliveryWeightUnlocked = false;
      this.localData.full_delivery_weight_kg = null;
    },
    async onWeighDelivery() {
      if (!this.canWeighDelivery || this.deliveryWeightLoadState === "loading") {
        return;
      }

      this.deliveryWeightLoadState = "loading";

      const hasMeasurement = await this.fetchScaleMeasurementAndApply(this.localData.full_delivery_scale_uuid, "full_delivery_weight_kg", "full_delivery_datetime");

      this.deliveryWeightLoadState = hasMeasurement ? "success" : "error";
    },
    onDeliveryWeightKeyUp(e) {
      this.deliveryWeightLoadState = "";
      this.localData.full_delivery_datetime = moment().format(this.getDefaultDateFormat + " HH:mm:ss");
    },
    getWeightStatusMessage(state) {
      if (state === "loading") {
        return this.$t("scale.weighing_on_scale.weight_loading_in_progress");
      }

      if (state === "success") {
        return this.$t("scale.weighing_on_scale.weight_loaded");
      }

      if (state === "error") {
        return this.$t("scale.weighing_on_scale.weight_not_found");
      }

      return "";
    },
    getWeightStatusClass(state) {
      return {
        "weight-status-text": true,
        "is-loading": state === "loading",
        "is-success": state === "success",
        "is-error": state === "error",
      };
    },
    async fetchScaleMeasurementAndApply(scaleUuid, weightField, datetimeField) {
      if (!scaleUuid) {
        return false;
      }

      try {
        const response = await this.getScaleMeasurement({ scale_uuid: scaleUuid });
        const responseData = response?.data;
        const measurements = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
          ? responseData
          : Array.isArray(this.scaleMeasurements)
          ? this.scaleMeasurements
          : [];

        if (!Array.isArray(measurements) || measurements.length === 0) {
          return false;
        }

        const measurement = measurements[0];
        if (!measurement || measurement.weight === null || measurement.weight === undefined) {
          return false;
        }

        const parsedWeight = Number(measurement.weight);
        if (!Number.isFinite(parsedWeight) || parsedWeight < 0) {
          return false;
        }

        this.localData[weightField] = parsedWeight;
        this.localData[datetimeField] = measurement.measurement_time || moment().format(this.getDefaultDateFormat + " HH:mm:ss");

        return true;
      } catch (error) {
        return false;
      }
    },
    validate() {
      /*
      this.$v.$touch(); // Trigger validation
      if (this.$v.$invalid) {
        return false;
      }
      */
      return {
        error: false,
        message: "",
      };
    },
    updateData(newData) {
      if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
        //console.log("updateData step 2", newData);
        this.localData = { ...newData }; // Update localData with new data
        //this.applyDefaultScaleSelection();
      }
    },
  },
  mounted() {
    if (this.localData?.scale_house_uuid && (!Array.isArray(this.scaleOptions) || this.scaleOptions.length === 0)) {
      this.getScales({ scale_house_uuid: this.localData.scale_house_uuid }).finally(() => {
        this.applyDefaultScaleSelection(true);
      });
      return;
    }

    this.applyDefaultScaleSelection();
  },
  watch: {
    scales: {
      immediate: true,
      deep: true,
      handler() {
        this.applyDefaultScaleSelection();
      },
    },
    "localData.scale_house_uuid": function (newValue, oldValue) {
      const forceSelection = !!oldValue && newValue !== oldValue;
      this.applyDefaultScaleSelection(forceSelection);
    },
    localData: {
      deep: true,
      handler(newData) {
        this.$emit("update-form-data", newData, 2);
      },
    },
  },
  /*validations() {
    return {
      localData: {
        empty_weight_kg: {
          required: requiredIf(function () {
            return !this.localData.full_weight_kg; // Required if full_weight_kg is empty
          }),
        },
        full_weight_kg: {
          required: requiredIf(function () {
            return !this.localData.empty_weight_kg; // Required if empty_weight_kg is empty
          }),
        },
      },
    };
  },*/
};
</script>

<style scoped>
.wos-scaling {
  height: 100%;
  width: 100%;
}

.scaling-boxes {
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  flex-wrap: wrap;
  height: 90%;
  width: 100%;
}

.load-mass {
  display: block;
  text-align: center;
  justify-content: center;
  align-items: center;
  height: 10%;
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-top: 15px;
  margin-bottom: 10px;
  gap: 100px;
  width: 100%;
}

.load-mass-delivery {
  color: #206bc4;
}

.empty-box {
  height: 100%;
  width: 30%;
  border: 2px solid green;
  padding: 10px;
}

.full-box {
  height: 100%;
  width: 30%;
  border: 2px solid orange;
  padding: 10px;
}

.delivery-box {
  height: 100%;
  width: 30%;
  border: 2px solid #206bc4;
  padding: 10px;
}

.weight-box-title {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 15px;
}

.measurement-rows {
  padding-top: 10px;
  padding-left: 10px;
  justify-content: left;
  align-items: left;
}

.weight-status-row {
  min-height: 18px;
  margin-top: 6px;
}

.weight-status-text {
  font-size: 12px;
  font-weight: 600;
}

.weight-status-text.is-loading {
  color: #206bc4;
}

.weight-status-text.is-success {
  color: #1a8f3d;
}

.weight-status-text.is-error {
  color: #d93025;
}

.btn-disbled {
  background-color: #ccc !important;
  color: #333 !important;
  cursor: not-allowed !important;
  border: none !important;
}

.different-quantity-row {
  padding-top: 10px;
}

.different-quantity-row ::v-deep .dx-checkbox-text {
  font-weight: bold;
}
</style>
